/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { Text, Title } from '@components/v2/ui';
import {
	ActionIcon,
	Affix,
	Box,
	Flex,
	Group,
	List,
	Stack,
	Transition,
} from '@mantine/core';
import { useWindowScroll } from '@mantine/hooks';
import {
	IconArrowNarrowLeft,
	IconArrowNarrowUp,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router';
import classe from './cgu.module.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const CGUEN = () => {
	const [scroll, scrollTo] = useWindowScroll();
	const navigate = useNavigate();

	const handleGoToPreviousPage = () => {
		navigate(-1);
	};
	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classe.container} w="100%">
			<Box className={classe.header}>
				<Flex
					justify={'space-between'}
					gap={'12px'}
					align={'center'}
					wrap={'wrap'}
				>
					<Group
						style={{
							cursor: 'pointer',
						}}
						onClick={handleGoToPreviousPage}
					>
						<ActionIcon
							color={'#ADD2EA'}
							variant="outline"
							size="48px"
							radius="xl"
							aria-label="Settings"
						>
							<IconArrowNarrowLeft
								style={{ width: '70%', height: '70%' }}
								stroke={1.5}
								color={'#727272'}
							/>
						</ActionIcon>
						<Box>
							<Text
								style={{
									cursor: 'pointer',
								}}
								c={'#1D3965'}
								variant="body1"
							>
								Back to the previous page
							</Text>
						</Box>
					</Group>

					<Text c={'#1D3965'} variant="body1">
						Updated at : 19/11/2025 10:18
					</Text>
				</Flex>
			</Box>

			<Box className={classe.content}>
				<Stack gap={'5px'}>
					<Title
						style={{
							textAlign: 'center',
						}}
						c={'#1D3965'}
						order={3}
					>
						Terms and Conditions of Use <br />
						for the Iglu platform
					</Title>
				</Stack>

				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						0. Definitions
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						Customer (hereinafter "Customer"): natural or
						legal person who has entered into a contract
						with Inuk to purchase carbon credits.
						<br />
						<br />
						Producer (hereinafter "Producer"): legal
						entity whose activity is the development of
						decarbonisation projects – projects that are
						listed on the Iglu Platform – and which is
						authorised to transmit and monitor its
						production data.
						<br />
						<br />
						Partner (hereinafter "Partner"): legal entity
						whose activity is the management of a
						portfolio of decarbonisation projects and
						clients on the Iglu Platform, by accessing
						project monitoring data, production data and
						offsets achieved by Clients.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						1. Purpose
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						The purpose of these Terms and Conditions of
						Use (hereinafter "TCU") is to define the terms
						and conditions of access and use of the Iglu
						platform (hereinafter "the Platform"),
						published and operated by Inuk (hereinafter
						"Inuk"). Iglu is a digital solution that
						allows:
						<br />
						<br />
						<List>
							<List.Item>
								Customers: view their carbon offset
								transactions, access their transaction
								history and associated certificates;
							</List.Item>
							<List.Item>
								Producers: manage their INUK-certified
								renewable energy production projects,
								transmit their production data and monitor
								it;
							</List.Item>
							<List.Item>
								Partners: manage a portfolio of projects
								and customers, accessing project
								monitoring data, production data and
								offsets achieved by Customers.
							</List.Item>
						</List>
						These Terms of Use constitute a legally
						binding contract between Inuk and the Platform
						User (hereinafter "the User"), regardless of
						their profile (Customer, Producer, Partner).
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						2. Contractual documents
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						The contractual relationship between the
						Client and Inuk is governed, in descending
						order of hierarchy, by the following
						documents:
						<br />
						<br />
						<List>
							<List.Item>
								- in the event of any inconsistency
								between these Terms and Conditions of Use
								of the Iglu platform and INUK's Standard
								Rules and Requirements, the latter shall
								prevail over the Terms and Conditions of
								Use of the Iglu platform. INUK Standard
								Rules and Requirements
							</List.Item>
							<List.Item>
								- the Customer accepts these Terms and
								Conditions by ticking a box on the
								registration form. If the Customer does
								not accept these Terms and Conditions of
								Use, they will not be able to access the
								Iglu Platform. Iglu Platform Terms and
								Conditions of Use
							</List.Item>
						</List>
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						3. Scope and acceptance
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						Access to the Platform is reserved for persons
						expressly authorised by Inuk within the
						framework of a contractual relationship.
						<br />
						<br />
						Accounts are created exclusively by Inuk
						teams, based on information provided by client
						organisations, producers or partners.
						<br />
						<br />
						By accessing the Platform, the User
						acknowledges that they have read these Terms
						of Use and accept them without reservation.
						Any use of the Platform implies full and
						complete acceptance of these terms and
						conditions.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						4. Account creation and management
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						<Text c={'#1D3965'} variant="subtitle1" span>
							4.1. Creation procedures
						</Text>
						<br />
						<br />
						<List>
							<List.Item>
								For Clients: access is automatically
								granted following the validation of a
								contribution request sent to Inuk. The
								contribution is considered validated from
								the date of actual receipt of payment of
								the invoice issued by Inuk to the Client.
							</List.Item>
							<List.Item>
								For Producers: access is granted upon
								certification and integration of a project
								on the Platform.
							</List.Item>
							<List.Item>
								For Partners: access is granted within the
								framework of a contractual partnership and
								is limited to the monitoring and
								management tasks entrusted by Inuk.
							</List.Item>
						</List>
						<br />
						<br />
						<Text c={'#1D3965'} variant="subtitle1" span>
							4.2. Data collected
						</Text>
						<br />
						<br />
						The following information is collected for the
						creation of accounts to access the platform:
						surname, first name, professional email
						address and, optionally, a professional
						telephone number.
						<br />
						<br />
						<Text c={'#1D3965'} variant="subtitle1" span>
							4.3. User responsibility
						</Text>
						<br />
						<br />
						The User is solely responsible for maintaining
						the confidentiality of their login details and
						for the use made of them. Any connection or
						transmission of data made using their login
						details will be deemed to have been made by
						them.
						<br />
						<br />
						The User undertakes to provide accurate
						information and to inform Inuk of any changes.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						5. Access rights and use of the Platform
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						<Text c={'#1D3965'} variant="subtitle1" span>
							5.1. Specific rights by profile
						</Text>
						<br />
						<br />
						<List>
							<List.Item>
								Client: access to offsets made,
								corresponding certificates and
								compensation history.
							</List.Item>
							<List.Item>
								Producer: access to their projects,
								transmission of production data,
								monitoring of production history.
							</List.Item>
							<List.Item>
								Partner/manager: access to monitored
								projects, production data and information
								relating to customers and their offsets.
							</List.Item>
						</List>
						<br />
						<br />
						<Text c={'#1D3965'} variant="subtitle1" span>
							5.2. Rules of good use
						</Text>
						<br />
						<br />
						The User shall refrain from, in particular:
						<List>
							<List.Item>
								accessing or attempting to access data or
								areas not intended for them;
							</List.Item>
							<List.Item>
								compromising the integrity or proper
								functioning of the Platform;
							</List.Item>
							<List.Item>
								using the Platform for fraudulent or
								illegal purposes, or for purposes that do
								not comply with its intended use.
							</List.Item>
						</List>
						<br />
						<br />
						Any breach may result in the immediate
						suspension or deletion of the account by Inuk,
						without prejudice to any legal proceedings.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						6. Platform pricing
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						<Text c={'#1D3965'} variant="subtitle1" span>
							6.1. Customers and Producers
						</Text>
						<br />
						<br />
						Access to the Iglu Platform is free for
						Customers and Producers certified by Inuk.
						<br />
						<br />
						<Text c={'#1D3965'} variant="subtitle1" span>
							6.2. Partners
						</Text>
						<br />
						<br />
						Access for Partners/managers is subject to
						platform maintenance and development fees.
						Pricing terms and payment conditions are
						specified in the partnership agreement entered
						into with Inuk.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						7. Intellectual Property
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						The Iglu Platform, as well as all of its
						technical, graphic, textual and functional
						components (excluding data provided by Users),
						are the exclusive property of Inuk and
						protected by intellectual property law.
						<br />
						<br />
						Any unauthorised reproduction, representation,
						modification, adaptation or exploitation, in
						whole or in part, is strictly prohibited and
						may result in legal action.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						8. Responsibilities
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						<Text c={'#1D3965'} variant="subtitle1" span>
							8.1. Inuk's liability
						</Text>
						<br />
						<br />
						Inuk undertakes to use reasonable means to
						ensure continuous and secure access to the
						Platform. However, Inuk does not guarantee
						uninterrupted availability and cannot be held
						liable in the event of malfunction,
						interruption or loss of data attributable to
						third parties or to a case of force majeure.
						<br />
						<br />
						The data accessible on the Platform is for
						informational purposes only. Contractual
						documents (quotations, invoices, agreements)
						remain the only legally binding references.
						<br />
						<br />
						<Text c={'#1D3965'} variant="subtitle1" span>
							8.2. User responsibility
						</Text>
						<br />
						<br />
						The User is solely responsible for the use of
						the Platform and the information accessed. It
						is the User's responsibility to ensure that
						their equipment is compatible with the
						Platform and to take all necessary measures to
						protect their data and IT systems.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						9. Protection of personal data
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						Inuk collects and processes professional data
						(surname, first name, professional email
						address, optional telephone number) for the
						sole purposes of managing access to the
						Platform and providing the service.
						<br />
						<br />
						This processing is based on the contractual
						agreement between Inuk and its client
						organisations, producers or partners.
						<br />
						<br />
						In accordance with the General Data Protection
						Regulation (GDPR) and the French Data
						Protection Act, each User has the right to
						access, rectify, delete, restrict, object to
						and transfer their data.
						<br />
						<br />
						These rights may be exercised by sending a
						request to the contact address indicated in
						the "Contact" section below.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						10. Data hosting and traceability
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						<Text c={'#1D3965'} variant="subtitle1" span>
							10.1. Data hosting
						</Text>
						<br />
						<br />
						The Iglu Platform's data is hosted by OVH, a
						certified provider that meets current security
						and availability standards.
						<br />
						<br />
						<Text c={'#1D3965'} variant="subtitle1" span>
							10.2. Traceability
						</Text>
						<br />
						<br />
						All transactions carried out on the Iglu
						Platform are systematically recorded on a
						public blockchain, guaranteeing their
						immutability and traceability.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						11. Suspension and termination
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						Inuk reserves the right to suspend, restrict
						or remove a User's access to the Platform in
						the event of non-compliance with these Terms
						of Use, or at the request of the organisation
						to which they belong.
						<br />
						<br />
						The termination of the contractual
						relationship between Inuk and an organisation
						shall result in the deletion of the accounts
						linked to that organisation.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						12. Modification of the Terms of Use
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						Inuk reserves the right to modify these Terms
						of Use at any time, in particular to adapt
						them to legal, regulatory or technical
						developments.
						<br />
						<br />
						Users will be informed of these modifications
						by any appropriate means. Continued use of the
						Platform implies acceptance of the modified
						Terms of Use.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						13. Applicable law and jurisdiction
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						These Terms of Use are governed by French law.
						<br />
						<br />
						Any dispute relating to their interpretation,
						validity or execution shall be subject to the
						exclusive jurisdiction of the courts within
						the jurisdiction of Inuk's registered office,
						unless otherwise required by law.
					</Text>
				</Box>
				{/* Bloc text */}
				<Box>
					<Title
						pb={'15px'}
						pl={'24px'}
						c={'#1D3965'}
						order={4}
					>
						14. Contact
					</Title>
					<Text
						style={{
							textAlign: 'justify',
						}}
						c={'#1D3965'}
						variant="body1"
					>
						For any questions regarding the use of the
						Platform or the exercise of your rights under
						the GDPR, you may contact: <br />
						The data controller is: Rym Kadry, rym@inuk.co
						<br />
						Address: 13 rue Santeuil 75005 Paris
						<br />
						Email : tech@inuk.co
					</Text>
				</Box>
			</Box>

			<Box
				style={{
					marginTop: '96px',
					height: '38px',
					backgroundColor: '#1D3965',
				}}
			></Box>

			<Affix position={{ bottom: 20, right: 20 }}>
				<Transition
					transition="slide-up"
					mounted={scroll.y > 0}
				>
					{() => (
						<ActionIcon
							onClick={() => scrollTo({ y: 0 })}
							color={'#ADD2EA'}
							variant="outline"
							size="48px"
							radius="xl"
							aria-label="Settings"
						>
							<IconArrowNarrowUp
								style={{ width: '70%', height: '70%' }}
								stroke={1.5}
								color={'#727272'}
							/>
						</ActionIcon>
					)}
				</Transition>
			</Affix>
		</Box>
	);
};

export default CGUEN;
